import { useAppDispatch, useAppSelect } from '~/app/store';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import useToasts from '~/common/components/toasts/useToasts';

import ActivationBatchPoller from './ActivationBatchPoller';
import { ActivationCompletionCard } from './ActivationCompletionCard';
import { ActivationProgressCard } from './ActivationProgressCard';
import { ActivationBatchStatus } from './activationProgress.types';
import {
  getPresentationGroupKey,
  groupBatchesByPresentation,
  isBulkBatch,
  summarizeCompletedBatches,
} from './batch.utils';
import {
  getPresentationHandlers,
  usePresentationHandlersRevision,
} from './presentationHandlers';
import { isBatchRetryable } from './retry.utils';
import { removeActivationBatch } from './state/activationProgressSlice';
import { selectActiveActivationBatches } from './state/activationProgressSlice.selectors';
import { useRetryActivationBatch } from './useRetryActivationBatch';
import { useRetryingBatchIds } from './useRetryingBatchIds';

/**
 * Global, persistent bottom-right activation widget, mounted once from
 * `AppContent` (alongside the vehicle-import `PendingWorkflowsSnackbar`).
 *
 * Bulk batches are split into presentation groups (see
 * {@link groupBatchesByPresentation}) and each gets its own card: while any
 * batch in the group is running it shows the {@link ActivationProgressCard};
 * once the whole group is terminal it switches in place to the
 * {@link ActivationCompletionCard} with that group's aggregate counts. Two
 * groups can be stacked at once (e.g. a Coast enrollment finishing alongside
 * a camera activation) without mixing their titles, text, or counts. A
 * group's "try again" button calls its registered presentation-group handler
 * when supplied, or the built-in peripheral retry-activate flow otherwise.
 * Single-item activations (see
 * {@link isBulkBatch}) are polled so their toast fires and the list
 * refetches, but are never shown here.
 */
const ActivationProgressSnackbar = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const batches = useAppSelect(selectActiveActivationBatches);
  const { showErrorToast } = useToasts();
  const { retry, retryingBatchIds } = useRetryActivationBatch();
  // Separate in-flight tracking for custom integrations' own
  // `onRetryBatches`, kept apart from the peripheral hook's so the two
  // mechanisms can run concurrently without clobbering each other.
  const { retryingBatchIds: customRetryingBatchIds, track: trackCustomRetry } =
    useRetryingBatchIds();
  usePresentationHandlersRevision();

  if (batches.length === 0) {
    return null;
  }

  // Headless pollers stay mounted for every in-progress batch (including
  // single-device) so completion is detected regardless of the visible view.
  const inProgress = batches.filter(
    (batch) => batch.status === ActivationBatchStatus.IN_PROGRESS
  );
  const pollers = inProgress.map((batch) => (
    <ActivationBatchPoller key={batch.batchId} batch={batch} />
  ));

  const groups = groupBatchesByPresentation(batches.filter(isBulkBatch));

  const cards = groups.map((group) => {
    const groupKey = getPresentationGroupKey(group[0]);
    const presentationHandlers = getPresentationHandlers(groupKey);

    // Progress view while any bulk batch in this group is still running.
    const inProgressBulk = group.filter(
      (batch) => batch.status === ActivationBatchStatus.IN_PROGRESS
    );
    if (inProgressBulk.length > 0) {
      return <ActivationProgressCard key={groupKey} batches={inProgressBulk} />;
    }

    // Completion view once every bulk batch in this group is terminal.
    const completedBulk = group.filter(
      (batch) => batch.status !== ActivationBatchStatus.IN_PROGRESS
    );
    if (completedBulk.length === 0) return null;

    const summary = summarizeCompletedBatches(completedBulk);
    // Batches with retryable failures drive the "try again" action; hidden when
    // everything succeeded or no failed devices can be safely targeted (a
    // partial success with unknown failed ids yields no retry target). Uses the
    // same predicate as the retry flow so the button never shows for a batch the
    // retry would skip.
    const failedBatches = completedBulk.filter(isBatchRetryable);
    // A custom integration's own retry takes priority over the built-in
    // peripheral one; every batch in a group shares the same mechanism by
    // construction (they're all from the same caller).
    const customRetry = presentationHandlers?.onRetryBatches;
    // Scoped to this group: a retry in flight for a sibling group must not
    // disable this card's retry button or block its close button.
    const isGroupRetrying = completedBulk.some(
      (batch) =>
        retryingBatchIds.includes(batch.batchId) ||
        customRetryingBatchIds.includes(batch.batchId)
    );

    return (
      <ActivationCompletionCard
        key={groupKey}
        successCount={summary.successCount}
        failureCount={summary.failureCount}
        reenrollmentSuccessCount={summary.reenrollmentSuccessCount}
        reenrollmentFailureCount={summary.reenrollmentFailureCount}
        enrolledVehicleCount={summary.enrolledVehicleCount}
        productName={summary.productName}
        completeTitle={summary.completeTitle}
        activatedText={summary.activatedText}
        failedText={summary.failedText}
        {...(summary.primaryActionLabel &&
          summary.onPrimaryAction && {
            primaryActionLabel: summary.primaryActionLabel,
            onPrimaryAction: summary.onPrimaryAction,
          })}
        {...(failedBatches.length > 0 && {
          onRetry: customRetry
            ? () => {
                void trackCustomRetry(
                  failedBatches.map((batch) => batch.batchId),
                  () => customRetry(failedBatches)
                ).catch(() => {
                  showErrorToast(
                    translateText('catalog:order.activationProgress.error')
                  );
                });
              }
            : () => retry(failedBatches),
          isRetrying: isGroupRetrying,
        })}
        // Block dismissal while this group's retry is in flight: the retry
        // resolves into `replaceRetriedBatches`, so clearing here would make
        // the card vanish and then reappear when the request settles. Removes
        // only this group's batches so a sibling group's card is unaffected.
        onClose={() => {
          if (isGroupRetrying) return;
          for (const batch of completedBulk) {
            dispatch(removeActivationBatch(batch.batchId));
          }
        }}
      />
    );
  });

  return (
    <>
      {pollers}
      {cards}
    </>
  );
};

export default ActivationProgressSnackbar;
